import { FinancialPropertyDefinition } from './qualityTypes';

export const FINANCIAL_PROPERTIES_REGISTRY: FinancialPropertyDefinition[] = [
  {
    propertyId: 'PROP-LOAN-MONOTONICITY-001',
    modelKey: 'loan_mortgage_amortization',
    name: 'Loan Monthly Payment Monotonicity with respect to Principal',
    type: 'MONOTONICITY',
    description: 'For any fixed interest rate r > 0 and fixed amortization term n > 0, if Principal P1 > P2, then PMT(P1) > PMT(P2).',
    preconditions: 'P1 > P2 > 0, r in (0, 0.30], n in [1, 360]',
    invariantStatement: 'calcMonthlyPayment(P1, r, n) >= calcMonthlyPayment(P2, r, n)',
    generatorSchema: 'fast-check: fc.tuple(fc.float({min: 1000, max: 5000000}), fc.float({min: 1000, max: 5000000}), fc.float({min: 0.001, max: 0.30}), fc.integer({min: 12, max: 360}))',
    riskLevel: 'CRITICAL',
    runsPerCI: 500,
    deepRunsNightly: 10000,
    seedPolicy: 'DETERMINISTIC_PRNG_WITH_TIMESTAMP_OFFSET',
    owner: 'Elena Rostova (Principal Financial Model Validation Architect)',
    lastRunStatus: 'PASSED',
    lastSeed: 1740441920
  },
  {
    propertyId: 'PROP-LOAN-EXTRA-PAYMENT-002',
    modelKey: 'loan_mortgage_amortization',
    name: 'Extra Principal Conservation & Interest Reduction Invariant',
    type: 'INVARIANT',
    description: 'Adding a positive extra principal payment strictly decreases or preserves total interest paid over the life of the loan.',
    preconditions: 'P > 0, r > 0, n > 0, extraPMT > 0',
    invariantStatement: 'totalInterest(P, r, n, extraPMT) < totalInterest(P, r, n, 0)',
    generatorSchema: 'fast-check: fc.record({ principal: fc.float({min: 10000, max: 1000000}), rate: fc.float({min: 0.01, max: 0.20}), termMonths: fc.integer({min: 24, max: 360}), extra: fc.float({min: 10, max: 2000}) })',
    riskLevel: 'CRITICAL',
    runsPerCI: 500,
    deepRunsNightly: 10000,
    seedPolicy: 'DETERMINISTIC_PRNG_WITH_TIMESTAMP_OFFSET',
    owner: 'Elena Rostova',
    lastRunStatus: 'PASSED',
    lastSeed: 1740441921
  },
  {
    propertyId: 'PROP-TAX-NET-MONOTONICITY-003',
    modelKey: 'salary_us_fed_state',
    name: 'Marginal Net Take-Home Monotonicity (No Cliff Effect)',
    type: 'MONOTONICITY',
    description: 'For any gross salary G1 > G2, Net(G1) >= Net(G2), proving marginal effective tax rate never exceeds 100%.',
    preconditions: 'G1 > G2 >= 0, single filer, standard deduction',
    invariantStatement: 'calculateNetPaycheck(G1).netAnnual >= calculateNetPaycheck(G2).netAnnual',
    generatorSchema: 'fast-check: fc.tuple(fc.float({min: 0, max: 2000000}), fc.float({min: 0, max: 2000000}))',
    riskLevel: 'CRITICAL',
    runsPerCI: 1000,
    deepRunsNightly: 25000,
    seedPolicy: 'DETERMINISTIC_PRNG_WITH_TIMESTAMP_OFFSET',
    owner: 'Sarah Jenkins',
    lastRunStatus: 'PASSED',
    lastSeed: 1740441922
  },
  {
    propertyId: 'PROP-FX-INVERSE-ROUNDTRIP-004',
    modelKey: 'fx_cross_conversion',
    name: 'Mid-Market FX Inverse Rate Round-Trip Precision Invariant',
    type: 'ROUND_TRIP',
    description: 'Converting amount A from currency X to Y at mid-rate R, then from Y to X at 1/R reconstructs original amount A within float precision epsilon (0.0001%).',
    preconditions: 'Amount > 0, Rate > 0',
    invariantStatement: 'Math.abs((Amount * R * (1/R)) - Amount) < 1e-6 * Amount',
    generatorSchema: 'fast-check: fc.record({ amount: fc.float({min: 1, max: 10000000}), rate: fc.float({min: 0.0001, max: 10000}) })',
    riskLevel: 'HIGH',
    runsPerCI: 1000,
    deepRunsNightly: 20000,
    seedPolicy: 'DETERMINISTIC_PRNG_WITH_TIMESTAMP_OFFSET',
    owner: 'Chen Wei',
    lastRunStatus: 'PASSED',
    lastSeed: 1740441923
  },
  {
    propertyId: 'PROP-SPLIT-PENNY-CONSERVATION-005',
    modelKey: 'everyday_margin_markup_split',
    name: 'Split Bill Total Sum Exact Minor-Unit Conservation',
    type: 'CONSERVATION',
    description: 'Dividing any bill of C cents among N people results in N discrete cent shares whose exact integer sum equals C.',
    preconditions: 'TotalCents in [1, 10000000], N in [1, 50]',
    invariantStatement: 'splitShares(TotalCents, N).reduce((acc, s) => acc + s, 0) === TotalCents',
    generatorSchema: 'fast-check: fc.record({ totalCents: fc.integer({min: 1, max: 10000000}), diners: fc.integer({min: 1, max: 50}) })',
    riskLevel: 'MEDIUM',
    runsPerCI: 500,
    deepRunsNightly: 10000,
    seedPolicy: 'DETERMINISTIC_PRNG_WITH_TIMESTAMP_OFFSET',
    owner: 'Tariq Al-Mansoor',
    lastRunStatus: 'PASSED',
    lastSeed: 1740441924
  },
  {
    propertyId: 'PROP-MARGIN-MARKUP-INEQUALITY-006',
    modelKey: 'everyday_margin_markup_split',
    name: 'Commercial Margin vs Markup Strict Inequality Invariant',
    type: 'INVARIANT',
    description: 'For all goods where Price > Cost > 0, Gross Margin Percentage is strictly less than Markup Percentage (Margin < Markup).',
    preconditions: 'Price > Cost > 0',
    invariantStatement: 'grossMarginPercent(Cost, Price) < markupPercent(Cost, Price)',
    generatorSchema: 'fast-check: fc.record({ cost: fc.float({min: 0.01, max: 100000}), price: fc.float({min: 0.02, max: 200000}) }).filter(r => r.price > r.cost)',
    riskLevel: 'HIGH',
    runsPerCI: 500,
    deepRunsNightly: 10000,
    seedPolicy: 'DETERMINISTIC_PRNG_WITH_TIMESTAMP_OFFSET',
    owner: 'Tariq Al-Mansoor',
    lastRunStatus: 'PASSED',
    lastSeed: 1740441925
  }
];
